import { twMerge } from 'tailwind-merge'
import { clsx, type ClassValue } from 'clsx'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return 'Invalid Date'
  }
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const target = new Date(date)
  const diffInSeconds = Math.floor((now.getTime() - target.getTime()) / 1000)

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60)
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minutes ago`
  }

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) {
    return `${diffInHours} hours ago`
  }

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) {
    return `${diffInDays} days ago`
  }

  return formatDate(date)
}

// ============= Duration Formatting =============

export function formatDuration(startedAt?: string, completedAt?: string): string | null {
  if (!startedAt || !completedAt) return null
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 1000) return '<1s'
  const totalSeconds = Math.floor(ms / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes < 60) return `${minutes}m ${seconds}s`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours}h ${remainingMinutes}m`
}

// ============= Case Conversion Utilities =============
// Backend uses snake_case, frontend uses camelCase

export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

export function keysToCamel<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToCamel(item)) as T
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[snakeToCamel(key)] = PASSTHROUGH_VALUE_KEYS.has(key) ? value : keysToCamel(value)
    }
    return result as T
  }

  return obj as T
}

// ============= Image Channel Utilities =============

/**
 * Derive APT channel from version string.
 * Matches ship-packer convention: v1.2.3 → stable, -beta.N → beta, -dev.N → unstable
 */
export function aptChannelFromVersion(version: string): string {
  const v = version.replace(/^v/, '')
  const match = v.match(/-([a-z]+)/)
  if (!match) return 'stable'
  switch (match[1]) {
    case 'beta':
    case 'rc':
      return 'beta'
    case 'testing':
      return 'testing'
    case 'dev':
      return 'unstable'
    default:
      return 'stable'
  }
}

const ENV_CHANNEL_MAP: Record<string, string> = {
  dev: 'unstable',
  test: 'testing',
  beta: 'beta',
  production: 'stable',
}

/**
 * Map VITE_ENVIRONMENT to the allowed APT channel.
 * Returns undefined for local dev (no filtering).
 */
export function channelForEnvironment(env?: string): string | undefined {
  if (!env) return undefined
  return ENV_CHANNEL_MAP[env]
}

// Keys whose values are passed through without case conversion.
// Helm value overrides use dotted camelCase paths (e.g. "networkPolicy.additionalEgressPorts[0].port")
// that must not be snake_cased.
const PASSTHROUGH_VALUE_KEYS = new Set([
  'values_override',
  'valuesOverride',
  'helm_overrides',
  'helmOverrides',
  'helm_overrides_string',
  'helmOverridesString',
])

export function keysToSnake<T>(obj: unknown): T {
  if (Array.isArray(obj)) {
    return obj.map(item => keysToSnake(item)) as T
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = camelToSnake(key)
      result[snakeKey] = PASSTHROUGH_VALUE_KEYS.has(key) ? value : keysToSnake(value)
    }
    return result as T
  }

  return obj as T
}
